/**
 * Detect if the app is running inside a native Capacitor shell (iOS/Android).
 * Falls back gracefully on the web (returns false). Used by MobileShell to
 * decide whether to render bottom-tabs nav instead of the desktop sidebar.
 */
import { useEffect, useState } from "react";

let _cachedIsNative: boolean | null = null;

export function isNativePlatform(): boolean {
  if (_cachedIsNative !== null) return _cachedIsNative;
  try {
    // Lazy require so SSR/non-cap envs don't crash
    // eslint-disable-next-line @typescript-eslint/no-var-requires
    const { Capacitor } = require("@capacitor/core");
    _cachedIsNative = !!Capacitor?.isNativePlatform?.();
  } catch {
    _cachedIsNative = false;
  }
  return _cachedIsNative;
}

export function getPlatform(): "ios" | "android" | "web" {
  try {
    // eslint-disable-next-line @typescript-eslint/no-var-requires
    const { Capacitor } = require("@capacitor/core");
    const p = Capacitor?.getPlatform?.();
    return p === "ios" || p === "android" ? p : "web";
  } catch {
    return "web";
  }
}

const MOBILE_BREAKPOINT = 768;

export function useIsMobile(): boolean {
  const [isMobile, setIsMobile] = useState<boolean>(() => {
    if (typeof window === "undefined") return false;
    return window.innerWidth < MOBILE_BREAKPOINT || isNativePlatform();
  });

  useEffect(() => {
    if (isNativePlatform()) {
      setIsMobile(true);
      return;
    }
    const onResize = () => setIsMobile(window.innerWidth < MOBILE_BREAKPOINT);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  return isMobile;
}
